import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSolarEdgePlants } from "../utils/solarEdgeUtils";
import {
  getPlantListFoxess,
  plantDetailsFoxess,
  getPlantPowerGenerationsFoxess,
} from "../utils/foxessUtils";
import {
  getStationList,
  type Station,
} from "../utils/huaweiUtils";
import LoadingSpinner from "./LoadingSpinner";
import { Link } from "react-router-dom";
import { useUserData } from "../context/UserDataContext";

type SolarEdgeInstallation = {
  id: number;
  name: string;
  peakPower: number;
};

const InstalationsList = ({ onTotalPeakPowerChange }: any) => {
  const [info, setInfo] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [detailsFoxEssList, setFoxEssDetailsList] = useState([]);
  const { userData } = useUserData();
  const solarEdgeToken = userData.solarEdgeToken;

  useEffect(() => {
    if (!solarEdgeToken) {
      setInfo(true);
    }
  }, [solarEdgeToken]);

  // 🌞 SolarEdge
  const {
    data: solaredgeInstalations = [],
    isLoading: loadingSolarEdge,
    refetch: refetchSolarEdge,
  } = useQuery<SolarEdgeInstallation[]>({
    queryKey: ["solarEdgeData"],
    queryFn: async () => {
      const res = await getSolarEdgePlants(solarEdgeToken);
      return res.data.sites.site;
    },
    enabled: !!solarEdgeToken,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: true,
  });

  // 🦊 FoxESS
  const {
    data: foxplantList = [],
    isLoading: loadingFoxEss,
    refetch: refetchFoxEss,
  } = useQuery({
    queryKey: ["foxEssData"],
    queryFn: async () => {
      const res = await getPlantListFoxess();
      return res.data.result.data;
    },
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: true,
  });

  // ☀️ Huawei
  const {
    data: huaweiList = [],
    isLoading: loadingHuawei,
    refetch: refetchHuawei,
  } = useQuery<Station[]>({
    queryKey: ["huaweiData"],
    queryFn: async () => {
      const res = await getStationList();
      return res ?? []; // zabezpieczenie na wypadek `null`
    },
    enabled: !!userData.fusionSolarPassword,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: true,
  });
  

  const loading = loadingSolarEdge || loadingFoxEss || loadingHuawei;

  // ✅ Obliczanie sumy mocy SolarEdge (możesz dodać też inne systemy)
  useEffect(() => {
    const newTotal = Object.keys(checkedItems).reduce((sum, id) => {
      if (checkedItems[id]) {
        const installation = solaredgeInstalations.find(
          (item) => item.id === parseInt(id)
        );
        return sum + (installation ? installation.peakPower : 0);
      }
      return sum;
    }, 0);

    onTotalPeakPowerChange(newTotal);
  }, [checkedItems, solaredgeInstalations, onTotalPeakPowerChange]);

  const handleCheckboxChange = (se: { id: any; name?: string; peakPower?: number; }) => {
    setCheckedItems((prevState) => ({
      ...prevState,
      [se.id]: !prevState[se.id],
    }));
  };

  return (
    <div id="InstalationList">
      <LoadingSpinner isLoading={loading} />
      <div
        className="box"
        style={{ width: "95%", minHeight: "300px", margin: 20 }}
      >
        <div className="savings-widget-title">
          Wybór instalacji do kalkulacji
          <button
            style={{
              color: "blue",
              marginLeft: "10px",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={() => {
              refetchSolarEdge();
              refetchFoxEss();
              refetchHuawei();
            }}
          >
            Ręczne odświeżenie danych
          </button>
        </div>

        {info && "Brak tokena do SolarEdge"}

        {loading ? (
          <div>Loading...</div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              color: "#7b7c7e",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f2f2f2", textAlign: "left" }}>
                <th style={{ padding: "8px", border: "1px solid #ddd" }}>Link</th>
                <th style={{ padding: "8px", border: "1px solid #ddd" }}>ID</th>
                <th style={{ padding: "8px", border: "1px solid #ddd" }}>Nazwa instalacji</th>
                <th style={{ padding: "8px", border: "1px solid #ddd" }}>Produkcja</th>
                <th style={{ padding: "8px", border: "1px solid #ddd" }}>Wybór</th>
              </tr>
            </thead>
            <tbody>
              {/* SolarEdge */}
              {solaredgeInstalations.map((se: SolarEdgeInstallation, index: number) => (
                <tr key={index}>
                  <td>
                    <Link to={`/installations/solarEdge/${se.id}`}>Więcej</Link>
                  </td>
                  <td>{se.id}</td>
                  <td>{se.name}</td>
                  <td>{se.peakPower}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!checkedItems[se.id]}
                      onChange={() => handleCheckboxChange(se)}
                    />
                  </td>
                </tr>
              ))}

              {/* FoxEss */}
              {foxplantList.map((fe: { stationID: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; name: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }, index: React.Key | null | undefined) => (
                <tr key={index}>
                  <td>
                    <Link to={`/installations/foxEss/${fe.stationID}`}>Więcej</Link>
                  </td>
                  <td>{fe.stationID}</td>
                  <td>{fe.name}</td>
                  <td>NONE</td>
                  <td>
                    <input type="checkbox" />
                  </td>
                </tr>
              ))}

              {/* Huawei */}
              {Array.isArray(huaweiList) && huaweiList.map((hu, index) => (
                <tr key={index}>
                  <td>
                    <Link to={`/installations/huawei/${hu.plantCode}`}>Więcej</Link>
                  </td>
                  <td>{hu.plantCode}</td>
                  <td>{hu.plantName}</td>
                  <td>NONE</td>
                  <td>
                    <input type="checkbox" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InstalationsList;
